import { useGetBookingsQuery } from '@api/front-office/front-office.api.rtk';
import { usePostChargeToRoom } from '@api/pos/pos-transaction-api.rtk';
import DataTable from '@components/table/datatable';
import useDataTable from '@hooks/datatable/useDataTable';
import { UseDisclosureInterface } from '@hooks/useDisclosure';
import { ModalWrapper } from '@hooks/useModal';
import useToast from '@hooks/useToast';
import {
  selectPOSPrinter,
  selectPOSShiftID,
  selectPOSstationID,
  selectPOSTableOrdersTotal,
  selectPOSTableTransactionID,
} from '@modules/point-of-sale/slice/pos-selector';
import { clearPOSSelectedTable } from '@modules/point-of-sale/slice/pos-table-area.slice';
import { Search } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Divider,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectPropertyID } from '@store/selector/property.selector';
import { formatNumber } from '@utils/format';
import { hashSignature } from '@utils/security';
import { useState } from 'react';
import { usePOSWindowDrawerCtx } from '../pos-order-taking-window';
import useAppContext from '@hooks/useAppContext';
import { ConfirmationDialog } from '@hooks/useConfirmationDialog';

const columns: GridColDef[] = [
  { field: 'roomNumber', headerName: 'Room' },
  { field: 'roomCode', headerName: 'Type' },
  { field: 'caption', headerName: 'Guest Name', width: 200 },
  { field: 'bookingRemarks', headerName: 'Remarks', flex: 1 },
];

interface IRow {
  referenceID: string;
  roomNumber: string;
  roomCode: string;
  caption: string;
  bookingRemarks: string;
  folioNumber?: string;
}

const cardStackSx = {
  width: '100%',
  '.card': {
    flex: 1,
    minWidth: { xs: '300px', md: '200px' },
  },
  '.card-title': {
    color: '#637381',
  },
  '.card-info': {
    fontWeight: 500,
    fontSize: '22px',
  },
};

const POSChargeToRoomModal = ({
  modalDisclosure,
}: {
  modalDisclosure: UseDisclosureInterface;
}) => {
  const {
    //  startLoadingStatus,
    stopLoadingStatus,
  } = useAppContext();
  const { othersDrawerDisclosure } = usePOSWindowDrawerCtx();
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const [selectedRow, setSelectedRow] = useState<IRow>();
  const [searchValue, setSearchValue] = useState('');
  const { apiRef, handleSearchValueChange } = useDataTable();
  const stationID = useAppSelector(selectPOSstationID);
  const transactionID = useAppSelector(selectPOSTableTransactionID);
  const shiftID = useAppSelector(selectPOSShiftID);
  const propertyID = useAppSelector(selectPropertyID);
  const pathname = window.location.pathname?.split('/')?.[1];
  const [chargeToRoomDialog, setChargeToRoomDialog] = useState(false);
  const printerID = useAppSelector(selectPOSPrinter);
  const { data, isLoading, isFetching } = useGetBookingsQuery(
    {
      type: 'in-house',
      unAssignedRooms: false,
      refetch: stationID + modalDisclosure.isOpen,
    },
    {
      skip: !modalDisclosure.isOpen,
      refetchOnMountOrArgChange: true,
      selectFromResult: ({ data, ...item }) => ({
        ...item,
        data: data?.map((item) => item.booking),
      }),
    },
  );

  const [chargeToRoom, { isLoading: isCharging }] = usePostChargeToRoom();

  const total = useAppSelector(selectPOSTableOrdersTotal);

  const handleClose = () => {
    modalDisclosure.onClose();
    setTimeout(() => {
      setSelectedRow(undefined);
      setSearchValue('');
    }, 100);
  };

  const handleSave = async () => {
    try {
      if (!selectedRow) return;

      await chargeToRoom({
        transactionID,
        payload: {
          properties: [
            {
              property: {
                propertyID,
                shiftID,
                stationID,
                bookingID: selectedRow.referenceID,
                signature: hashSignature(
                  `${propertyID}${shiftID}${stationID}${selectedRow.referenceID}`,
                ),
              },
            },
          ],
        },
        printerRefID: printerID || '',
      })
        .unwrap()
        .then(async (res) => {
          if (res.results.responseMessage == 'Success') {
            toast({
              severity: 'success',
              message: 'Successfully charged to room',
            });
          } else {
            toast({
              severity: 'error',
              message: res.results.responseMessage,
            });
          }
        })
        .catch((err) => {
          toast({ severity: 'error', message: err });
        })
        .finally(() => stopLoadingStatus());
    } catch (error) {
      toast({ message: error, severity: 'warning' });
    } finally {
      handleClose();
      dispatch(clearPOSSelectedTable());
      othersDrawerDisclosure.onClose();
      stopLoadingStatus();
      setChargeToRoomDialog(false);
    }
  };

  return (
    <>
      <ModalWrapper
        {...{
          modalDisclosure,
          withHeader: {
            title: `Select Guest Room: `,
            onClose: handleClose,
          },
        }}
      >
        <Stack sx={{ flex: 1, minHeight: 0 }}>
          {/* CONTENT */}
          <Stack gap={3} sx={{ flex: 1, minHeight: 0 }}>
            {/* CARD */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              divider={<Divider orientation='vertical' flexItem />}
              gap={2}
              sx={cardStackSx}
            >
              <Stack className='card'>
                <Typography className='card-title'>Room No.</Typography>
                <Typography className='card-info'>
                  {selectedRow?.roomNumber ?? '-'}
                </Typography>
              </Stack>
              <Stack className='card'>
                <Typography className='card-title'>Guest Name</Typography>
                <Typography className='card-info'>
                  {selectedRow?.caption ?? '-'}
                </Typography>
              </Stack>
              <Stack className='card'>
                <Typography className='card-title'>Total Amount</Typography>
                <Typography className='card-info'>
                  {formatNumber(total, 2, 2)}
                </Typography>
              </Stack>
            </Stack>

            {/* SEARCH */}
            <TextField
              value={searchValue}
              size='small'
              placeholder='Search . . .'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='start'>
                    <Search sx={{ color: '#212121' }} />
                  </InputAdornment>
                ),
              }}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleSearchValueChange(e, setSearchValue)
              }
              // disabled={isLoading || isFetching}
            />

            {/* DATATABLE */}
            <DataTable
              apiRef={apiRef}
              getRowId={(row) => row.referenceID}
              columns={columns}
              rows={data ?? []}
              sx={{
                p: 0,
                '.MuiDataGrid-selectedRowCount': { display: 'none' },
              }}
              checkboxSelection={false}
              rowSelectionModel={selectedRow ? [selectedRow?.referenceID] : []}
              onRowClick={(data) =>
                setSelectedRow((prev) =>
                  prev?.referenceID === data.row.referenceID
                    ? undefined
                    : data.row,
                )
              }
              disableSearch
              disableColumnFilter
              disableColumnSelector
              fullHeight
              tableStyle={{
                padding: 0,
                height: 'unset',
              }}
              wrapperStyle={{
                height: 'unset',
                display: 'flex',
                flex: 1,
                minHeight: '300px',
              }}
              loading={isLoading || isFetching}
            />
          </Stack>

          {/* BUTTON */}
          <Stack
            direction='row'
            gap={2}
            sx={{
              position: 'sticky',
              bottom: 0,
              bgcolor: '#FFF',
              justifyContent: 'flex-end',
              borderTop: '1px solid #EEE',
              pt: 3,
            }}
          >
            <Button variant='outlined' onClick={handleClose}>
              Close
            </Button>
            <LoadingButton
              variant='contained'
              disabled={!selectedRow}
              onClick={() => {
                pathname === 'point-of-sale'
                  ? setChargeToRoomDialog(true)
                  : handleSave;
              }}
              loading={isCharging}
            >
              Save
            </LoadingButton>
          </Stack>
        </Stack>
      </ModalWrapper>
      <ConfirmationDialog
        dialogStatus='info'
        iconColor='info'
        open={chargeToRoomDialog}
        title={'Confirm Charge to Room'}
        desc={
          <Stack spacing={2}>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 400,
                textAlign: 'center',
              }}
            >
              Please confirm these details:
            </Typography>

            <Stack
              direction={'row'}
              divider={<Divider orientation='vertical' flexItem />}
              gap={2}
              sx={cardStackSx}
            >
              <Stack className='card'>
                <Typography className='card-title'>Room No.</Typography>
                <Typography className='card-info'>
                  {selectedRow?.roomNumber}
                </Typography>
              </Stack>
              <Stack className='card'>
                <Typography className='card-title'>Folio No.</Typography>
                <Typography className='card-info'>
                  {selectedRow?.folioNumber}
                </Typography>
              </Stack>
            </Stack>

            <Stack
              direction={'row'}
              divider={<Divider orientation='vertical' flexItem />}
              gap={2}
              sx={cardStackSx}
            >
              <Stack className='card'>
                <Typography className='card-title'>Guest Name:</Typography>
                <Typography className='card-info'>
                  {selectedRow?.caption}
                </Typography>
              </Stack>
              <Stack className='card'>
                <Typography className='card-title'>Amount:</Typography>
                <Typography className='card-info'>
                  {formatNumber(total, 2, 2)}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        }
        cancelButtonTitle='Cancel'
        onConfirm={handleSave}
        onClose={() => setChargeToRoomDialog(false)}
      />
    </>
  );
};

export default POSChargeToRoomModal;
